import {
  ArrowRight,
  CalendarIcon,
  CheckCircle,
  Clock,
  DollarSign,
  Eye,
  FileText,
  Link as LinkIcon,
  Loader2,
  Mail,
  Phone,
  Receipt,
  Trash2,
  Upload,
} from "lucide-react";
import { Doc, Id } from "@cvx/_generated/dataModel";
import { Link, useNavigate } from "@tanstack/react-router";
import { convexQuery, useConvexMutation } from "@convex-dev/react-query";
import { formatCurrency, formatDate } from "@/utils/format";
import { useMutation, useQuery } from "@tanstack/react-query";

import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { api } from "@cvx/_generated/api";
import exampleTemplateImage from "@/assets/example_template.png";
import { useStripeConnectAccount } from "@/hooks";

interface OverviewTabProps {
  contract: {
    status: "active" | "pendingApproval" | "completed" | "terminated";
    documents?: Doc<"creatorContracts">["documents"];
    creator: {
      id: string;
      name: string | undefined;
      profilePicture: string | undefined;
    };
    founderContact: {
      name: string | undefined;
      email: string | undefined;
      phone?: string | undefined;
    };
    contractTerms: {
      flatFeePerVideo: number;
      cpm: number;
      minimumViewsRequiredPerVideo: number;
      maximumPaymentCapPerVideo: number;
      currency: string;
    };
    deliverables?: {
      requiredVideoCount: number;
    };
    timeline?: Array<{
      date: string;
      event: string;
      status: "completed" | "upcoming" | "pending" | "rejected";
    }>;
    campaign: unknown;
  };
  partnership: {
    partnership: {
      _id: Id<"creatorContracts">;
      paidAmount?: number;
      paymentDate?: number;
      paymentReceipt?: string | Id<"_storage">;
      creatorInvoice?: string | Id<"_storage">;
    };
    app?: {
      name: string;
      legalCompanyName?: string;
      legalCompanyAddress?: string;
    };
  };
  contractRevisions:
    | Array<{
        _id: Id<"creatorContractRevisions">;
        _creationTime: number;
        status:
          | "pendingCreatorApproval"
          | "pendingAppFounderApproval"
          | "approved"
          | "rejected";
        newEndDate?: number;
        contractId: Id<"creatorContracts">;
      }>
    | undefined;
  paymentInfo: {
    totalEarnings: number;
    amountOwed: number;
    isWithinCutoffPeriod?: boolean;
    daysUntilCutoff?: number;
    cutoffPeriod?: number;
    cutoffDate?: Date;
  } | null;
  user: { _id?: Id<"users"> } | null | undefined;
  appId: string | undefined;
  creatorId: string | undefined;
  renderMainContent: () => JSX.Element;
  setShowDocumentUpload: (show: boolean) => void;
  setShowExtendContractDialog: (show: boolean) => void;
  setShowInvoiceUploadDialog: (show: boolean) => void;
  handleRemoveDocument: (documentName: string) => void;
  handleApproveReject: (
    revisionId: Id<"creatorContractRevisions">,
    status: "approved" | "rejected",
  ) => void;
  terminatePartnership: (params: {
    partnershipId: Id<"creatorContracts">;
  }) => Promise<unknown>;
  toast: (params: {
    title: string;
    description: string;
    variant?: "destructive";
  }) => void;
  isApprovingRejecting: boolean;
  formatRevisionDate: (timestamp: number | undefined) => string;
}

export default function OverviewTab({
  contract,
  partnership,
  contractRevisions,
  paymentInfo,
  user,
  appId,
  creatorId,
  renderMainContent,
  setShowDocumentUpload,
  setShowExtendContractDialog,
  setShowInvoiceUploadDialog,
  handleRemoveDocument,
  handleApproveReject,
  terminatePartnership,
  toast,
  isApprovingRejecting,
  formatRevisionDate,
}: OverviewTabProps) {
  const navigate = useNavigate();
  const {
    account,
    hasAccount,
    isLoading: isLoadingStripeAccount,
    isChecked: stripeAccountChecked,
    checkStripeConnectAccount,
  } = useStripeConnectAccount();

  // Fetch user's bank details
  const { data: bankDetails } = useQuery(
    convexQuery(api.app.getUserBankDetails, {}),
  );

  // Delete invoice mutation
  const deleteInvoiceMutation = useMutation({
    mutationFn: useConvexMutation(api.payouts.deleteCreatorInvoice),
    onSuccess: () => {
      toast({
        title: "Invoice deleted",
        description: "Your invoice has been deleted successfully",
      });
    },
    onError: (error) => {
      console.error("Error deleting invoice:", error);
      toast({
        title: "Error deleting invoice",
        description:
          error instanceof Error
            ? error.message
            : "There was an error deleting your invoice. Please try again.",
        variant: "destructive",
      });
    },
  });

  const handleDeleteInvoice = () => {
    if (!partnership?.partnership._id) return;

    if (
      confirm(
        "Are you sure you want to delete your invoice? This action cannot be undone.",
      )
    ) {
      deleteInvoiceMutation.mutate({
        contractId: partnership.partnership._id,
      });
    }
  };

  const handleInvoiceUploadClick = async () => {
    if (!user?._id) {
      toast({
        title: "Authentication required",
        description: "Please log in to upload an invoice",
        variant: "destructive",
      });
      return;
    }

    try {
      const result = await checkStripeConnectAccount();

      // Check if user has either Stripe Connect account or bank details
      const hasStripeAccount = result.hasAccount;
      const hasBankDetails = !!bankDetails;

      if (!hasStripeAccount && !hasBankDetails) {
        toast({
          title: "Payment method required",
          description:
            "Please set up either a Stripe Connect account or bank details in the billing settings before uploading an invoice.",
          variant: "destructive",
        });
        // Redirect to billing page
        navigate({ to: "/dashboard/settings/billing" });
        return;
      }

      // If they have at least one payment method, allow invoice upload
      setShowInvoiceUploadDialog(true);
    } catch (error) {
      console.error("Error checking payment setup:", error);
      toast({
        title: "Error checking payment setup",
        description:
          "There was an error checking your payment information. Please try again.",
        variant: "destructive",
      });
    }
  };

  return (
    <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
      {renderMainContent()}

      {/* Sidebar */}
      <div className="space-y-6 ">
        {!creatorId && (
          <Card>
            <div className="p-6 space-y-4">
              <div className="flex items-center justify-between">
                <h2 className="text-lg font-medium text-primary">Actions</h2>
              </div>
              <div className="flex flex-col gap-2">
                <Button
                  className="w-full"
                  onClick={() => setShowDocumentUpload(true)}
                >
                  <Upload className="h-4 w-4 mr-2" />
                  Upload Document
                </Button>
                {(contract.status === "active" ||
                  contract.status === "completed") &&
                  appId && (
                    <Button
                      variant="outline"
                      className="w-full"
                      disabled={contractRevisions?.some(
                        (revision) =>
                          revision.status === "pendingCreatorApproval" ||
                          revision.status === "pendingAppFounderApproval",
                      )}
                      onClick={() => setShowExtendContractDialog(true)}
                    >
                      <CalendarIcon className="h-4 w-4 mr-2" />
                      Extend Contract
                    </Button>
                  )}

                {contract.status === "active" && appId && (
                  <Button
                    variant="destructive"
                    className="w-full"
                    onClick={async () => {
                      try {
                        await terminatePartnership({
                          partnershipId: partnership.partnership._id,
                        });
                        toast({
                          title: "Contract terminated",
                          description:
                            "The contract has been terminated successfully",
                        });
                      } catch (error) {
                        console.error(error);
                        toast({
                          title: "Error terminating contract",
                          description:
                            error instanceof Error
                              ? error.message
                              : "Something went wrong",
                          variant: "destructive",
                        });
                      }
                    }}
                  >
                    Terminate Contract
                  </Button>
                )}
              </div>
            </div>
          </Card>
        )}

        {contract.documents && (
          <Card>
            <div className="p-6">
              <h2 className="text-lg font-medium text-primary">Documents</h2>
              <div className="mt-4 space-y-4">
                {contract.documents.map((doc, index) => (
                  <div
                    key={index}
                    className="flex items-center gap-3 rounded-lg bg-secondary p-3"
                  >
                    <FileText className="h-5 w-5 text-primary/60" />
                    <div className="flex-1 truncate">
                      <p className="truncate text-sm font-medium text-primary">
                        {doc.name}
                      </p>
                      <p className="text-xs text-primary/60">
                        Uploaded{" "}
                        {formatDate(new Date(doc.uploadedDate).toISOString())}
                      </p>
                    </div>
                    <div className="flex items-center gap-2">
                      <Button variant="ghost" size="icon" asChild>
                        <Link
                          to={
                            typeof doc.url === "string"
                              ? doc.url
                              : `/api/storage/${doc.url}`
                          }
                          target="_blank"
                        >
                          <LinkIcon className="h-4 w-4" />
                        </Link>
                      </Button>
                      {(appId || doc.uploadedBy === user?._id) && (
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleRemoveDocument(doc.name)}
                        >
                          <Trash2 className="h-4 w-4 text-destructive" />
                        </Button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </Card>
        )}

        {appId && (
          <Card>
            <div className="p-6">
              <h2 className="text-lg font-medium text-primary">Creator</h2>
              <div className="mt-4">
                <Button
                  variant="ghost"
                  className="w-full justify-between"
                  asChild
                >
                  <Link to={`/creators/${contract.creator.id}`}>
                    <div className="flex items-center gap-3">
                      <img
                        src={contract.creator.profilePicture}
                        alt={contract.creator.name}
                        className="h-8 w-8 rounded-full"
                      />
                      <span className="text-sm font-medium">
                        {contract.creator.name}
                      </span>
                    </div>
                    <ArrowRight className="h-4 w-4" />
                  </Link>
                </Button>
              </div>
            </div>
          </Card>
        )}

        {/* Payment Details Card for completed/terminated partnerships */}
        {(contract.status === "completed" ||
          contract.status === "terminated") && (
          <Card>
            <div className="p-6">
              <div className="flex items-center gap-3 mb-4">
                <DollarSign className="h-5 w-5 text-primary/60" />
                <h2 className="text-lg font-medium text-primary">
                  Payment Details
                </h2>
              </div>

              {partnership?.partnership.paidAmount &&
              partnership?.partnership.paymentDate ? (
                <div className="space-y-4">
                  <div className="flex items-center gap-2 text-green-600">
                    <CheckCircle className="h-4 w-4" />
                    <span className="text-sm font-medium">
                      Payment Completed
                    </span>
                  </div>

                  <div className="grid grid-cols-1 gap-4">
                    {paymentInfo && (
                      <div className="rounded-lg bg-secondary p-4">
                        <p className="text-sm text-primary/60">
                          Total Earnings
                        </p>
                        <p className="text-lg font-medium text-primary">
                          {formatCurrency(paymentInfo.totalEarnings)}
                        </p>
                      </div>
                    )}

                    <div className="rounded-lg bg-secondary p-4">
                      <p className="text-sm text-primary/60">Amount Paid</p>
                      <p className="text-lg font-medium text-primary">
                        {formatCurrency(
                          partnership.partnership.paidAmount || 0,
                        )}
                      </p>
                    </div>

                    <div className="rounded-lg bg-secondary p-4">
                      <p className="text-sm text-primary/60">Payment Date</p>
                      <p className="text-sm font-medium text-primary">
                        {partnership.partnership.paymentDate
                          ? formatDate(
                              new Date(
                                partnership.partnership.paymentDate,
                              ).toISOString(),
                            )
                          : "Not available"}
                      </p>
                    </div>

                    {/* Show appropriate receipt based on viewer role */}
                    {((appId &&
                      (partnership.partnership as Doc<"creatorContracts">)
                        ?.founderPaymentReceipt) ||
                      (creatorId && partnership.partnership.paymentReceipt) ||
                      (!appId &&
                        !creatorId &&
                        partnership.partnership.paymentReceipt)) && (
                      <div className="rounded-lg bg-secondary p-4">
                        <p className="text-sm text-primary/60">
                          Payment Receipt
                        </p>
                        <Button
                          variant="outline"
                          size="sm"
                          className="mt-2"
                          asChild
                        >
                          <Link
                            to={(() => {
                              // Show founder receipt for founders, creator receipt for creators
                              const receiptUrl = appId
                                ? (
                                    partnership.partnership as Doc<"creatorContracts">
                                  )?.founderPaymentReceipt ||
                                  partnership.partnership.paymentReceipt
                                : partnership.partnership.paymentReceipt;

                              return typeof receiptUrl === "string"
                                ? receiptUrl
                                : `/api/storage/${receiptUrl}`;
                            })()}
                            target="_blank"
                          >
                            <FileText className="h-4 w-4 mr-2" />
                            View Receipt
                          </Link>
                        </Button>
                      </div>
                    )}

                    {/* Show creator invoice if uploaded */}
                    {partnership?.partnership.creatorInvoice && (
                      <div className="rounded-lg bg-secondary p-4">
                        <p className="text-sm text-primary/60">
                          Creator Invoice
                        </p>
                        <div className="flex gap-2 mt-2">
                          <Button
                            variant="outline"
                            size="sm"
                            className="flex-1"
                            asChild
                          >
                            <Link
                              to={
                                typeof partnership.partnership
                                  .creatorInvoice === "string"
                                  ? partnership.partnership.creatorInvoice
                                  : `/api/storage/${partnership.partnership.creatorInvoice}`
                              }
                              target="_blank"
                            >
                              <Receipt className="h-4 w-4 mr-2" />
                              View Invoice
                            </Link>
                          </Button>
                          {/* Show delete button only for creators and if not paid yet */}
                          {creatorId &&
                            !partnership?.partnership.paidAmount &&
                            !partnership?.partnership.paymentDate && (
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={handleDeleteInvoice}
                                disabled={deleteInvoiceMutation.isPending}
                                className="text-destructive hover:text-destructive"
                              >
                                {deleteInvoiceMutation.isPending ? (
                                  <Loader2 className="h-4 w-4 animate-spin" />
                                ) : (
                                  <Trash2 className="h-4 w-4" />
                                )}
                              </Button>
                            )}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              ) : (
                <div className="space-y-4">
                  {paymentInfo?.isWithinCutoffPeriod ? (
                    <div className="flex items-center gap-2 text-blue-600">
                      <Clock className="h-4 w-4" />
                      <span className="text-sm font-medium">
                        Waiting for Cutoff Period
                      </span>
                    </div>
                  ) : !partnership?.partnership.creatorInvoice ? (
                    <div className="flex items-center gap-2 text-blue-600">
                      <Receipt className="h-4 w-4" />
                      <span className="text-sm font-medium">
                        {creatorId
                          ? "Upload your invoice to request payment"
                          : "Waiting for creator invoice"}
                      </span>
                    </div>
                  ) : (
                    <div className="flex items-center gap-2 text-orange-600">
                      <Clock className="h-4 w-4" />
                      <span className="text-sm font-medium">
                        Payment Pending
                      </span>
                    </div>
                  )}

                  {paymentInfo && paymentInfo.totalEarnings > 0 && (
                    <div className="rounded-lg bg-secondary p-4">
                      <p className="text-sm text-primary/60">Total Earnings</p>
                      <p className="text-lg font-medium text-primary">
                        {formatCurrency(paymentInfo.totalEarnings)}
                      </p>
                      {paymentInfo.amountOwed > 0 && (
                        <p className="text-xs text-primary/60 mt-1">
                          Amount owed: {formatCurrency(paymentInfo.amountOwed)}
                        </p>
                      )}
                    </div>
                  )}

                  {/* Show creator invoice if uploaded */}
                  {partnership?.partnership.creatorInvoice && (
                    <div className="rounded-lg bg-secondary p-4">
                      <p className="text-sm text-primary/60">Creator Invoice</p>
                      <div className="flex gap-2 mt-2">
                        <Button
                          variant="outline"
                          size="sm"
                          className="flex-1"
                          asChild
                        >
                          <Link
                            to={
                              typeof partnership.partnership.creatorInvoice ===
                              "string"
                                ? partnership.partnership.creatorInvoice
                                : `/api/storage/${partnership.partnership.creatorInvoice}`
                            }
                            target="_blank"
                          >
                            <Receipt className="h-4 w-4 mr-2" />
                            View Invoice
                          </Link>
                        </Button>
                        {/* Show delete button only for creators and if not paid yet */}
                        {creatorId &&
                          !partnership?.partnership.paidAmount &&
                          !partnership?.partnership.paymentDate && (
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={handleDeleteInvoice}
                              disabled={deleteInvoiceMutation.isPending}
                              className="text-destructive hover:text-destructive"
                            >
                              {deleteInvoiceMutation.isPending ? (
                                <Loader2 className="h-4 w-4 animate-spin" />
                              ) : (
                                <Trash2 className="h-4 w-4" />
                              )}
                            </Button>
                          )}
                      </div>
                    </div>
                  )}

                  <div className="rounded-lg bg-secondary p-4">
                    <p className="text-sm text-primary/60 mb-2">
                      Payment Status
                    </p>
                    {paymentInfo?.isWithinCutoffPeriod ? (
                      <p className="text-sm text-primary">
                        {creatorId
                          ? "Payment is on hold during the CPM cutoff period. We need to wait "
                          : "Payment is on hold during the CPM cutoff period. You need to wait "}
                        {paymentInfo.daysUntilCutoff} more day
                        {paymentInfo.daysUntilCutoff !== 1 ? "s" : ""} after the
                        contract ended to finalize CPM earnings before
                        processing payment.
                      </p>
                    ) : !partnership?.partnership.creatorInvoice ? (
                      creatorId ? (
                        <div className="space-y-3">
                          <p className="text-sm text-primary">
                            Please upload your invoice with payment details to
                            request payment. Once uploaded, the company will be
                            notified and can process your payment.
                          </p>

                          {/* Legal company information for invoice */}
                          {(partnership?.app?.legalCompanyName ||
                            partnership?.app?.legalCompanyAddress) && (
                            <div className="bg-emerald-50 dark:bg-emerald-950/20 p-3 rounded-md">
                              <p className="text-xs font-medium text-emerald-800 dark:text-emerald-200 mb-2">
                                Invoice To:
                              </p>
                              <div className="space-y-1">
                                {partnership?.app?.legalCompanyName && (
                                  <p className="text-xs text-emerald-700 dark:text-emerald-300 font-medium">
                                    {partnership.app.legalCompanyName}
                                  </p>
                                )}
                                {partnership?.app?.legalCompanyAddress && (
                                  <p className="text-xs text-emerald-700 dark:text-emerald-300">
                                    {partnership.app.legalCompanyAddress}
                                  </p>
                                )}
                              </div>
                            </div>
                          )}

                          {/* Example template button */}
                          <div className="bg-blue-50 dark:bg-blue-950/20 p-3 rounded-md">
                            <div className="flex items-center justify-between">
                              <div>
                                <p className="text-xs font-medium text-blue-800 dark:text-blue-200 mb-1">
                                  Need help with your invoice?
                                </p>
                                <p className="text-xs text-blue-700 dark:text-blue-300">
                                  View our example template to see the required
                                  format
                                </p>
                              </div>
                              <Button
                                variant="outline"
                                size="sm"
                                className="text-blue-600 border-blue-200 hover:bg-blue-100 dark:text-blue-400 dark:border-blue-800 dark:hover:bg-blue-900"
                                asChild
                              >
                                <Link
                                  to={exampleTemplateImage}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                >
                                  <Eye className="h-4 w-4 mr-2" />
                                  View Template
                                </Link>
                              </Button>
                            </div>
                          </div>

                          {/* Show payment method info if checked and has methods */}
                          {stripeAccountChecked &&
                            (hasAccount || bankDetails) && (
                              <div className="bg-green-50 dark:bg-green-950/20 p-3 rounded-md">
                                <div className="flex items-center gap-2 text-green-700 dark:text-green-300 mb-2">
                                  <CheckCircle className="h-4 w-4" />
                                  <span className="text-xs font-medium">
                                    Payment Method Ready
                                  </span>
                                </div>
                                <div className="space-y-2">
                                  {hasAccount && (
                                    <div className="flex items-center gap-2">
                                      <div className="w-6 h-4 bg-blue-600 rounded text-white text-xs flex items-center justify-center font-bold">
                                        SC
                                      </div>
                                      <span className="text-xs text-green-700 dark:text-green-300">
                                        {account?.business_profile?.name ||
                                          "Stripe Connect Account"}
                                      </span>
                                    </div>
                                  )}
                                  {bankDetails && (
                                    <div className="flex items-center gap-2">
                                      <div className="w-6 h-4 bg-emerald-600 rounded text-white text-xs flex items-center justify-center font-bold">
                                        BD
                                      </div>
                                      <span className="text-xs text-green-700 dark:text-green-300">
                                        Bank Details ({bankDetails.firstName}{" "}
                                        {bankDetails.lastName})
                                      </span>
                                    </div>
                                  )}
                                </div>
                              </div>
                            )}

                          {/* Show payment method warning if checked and no methods */}
                          {stripeAccountChecked &&
                            !hasAccount &&
                            !bankDetails && (
                              <div className="bg-amber-50 dark:bg-amber-950/20 p-3 rounded-md">
                                <p className="text-xs text-amber-800 dark:text-amber-200">
                                  ⚠️ You need to set up either a Stripe Connect
                                  account or bank details before uploading an
                                  invoice.
                                </p>
                                <Link
                                  to="/dashboard/settings/billing"
                                  className="text-xs text-amber-600 hover:text-amber-800 underline mt-1 inline-block"
                                >
                                  Set Up Payment Method
                                </Link>
                              </div>
                            )}

                          <Button
                            onClick={handleInvoiceUploadClick}
                            className="w-full"
                            disabled={isLoadingStripeAccount}
                          >
                            {isLoadingStripeAccount ? (
                              <>
                                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                                Checking...
                              </>
                            ) : (
                              <>
                                <Receipt className="h-4 w-4 mr-2" />
                                Upload Invoice
                              </>
                            )}
                          </Button>
                        </div>
                      ) : (
                        <p className="text-sm text-primary">
                          Waiting for the creator to upload their invoice with
                          payment details before payment can be processed.
                        </p>
                      )
                    ) : creatorId ? (
                      <p className="text-sm text-primary">
                        Your invoice has been uploaded and payment is being
                        processed. You will be notified once the payment is
                        completed and the receipt will be uploaded here.
                      </p>
                    ) : (
                      <div className="space-y-3">
                        <p className="text-sm text-primary">
                          The creator has uploaded their invoice and is ready to
                          be paid. You can now process the payment through the
                          payouts page.
                        </p>
                        <Button
                          onClick={() => navigate({ to: "/dashboard/payouts" })}
                          className="w-full"
                        >
                          Go to Payouts to Process Payment
                        </Button>
                      </div>
                    )}
                  </div>

                  <div className="rounded-lg border border-blue-200 bg-blue-50 p-4 dark:border-blue-800 dark:bg-blue-950">
                    {paymentInfo?.isWithinCutoffPeriod ? (
                      <p className="text-sm text-blue-800 dark:text-blue-200">
                        <strong>Why the wait?</strong> The contract includes a{" "}
                        {paymentInfo.cutoffPeriod}-day cutoff period to ensure
                        all CPM earnings are accurately calculated from videos
                        posted near the contract end date.
                        {paymentInfo.cutoffDate && (
                          <>
                            {" "}
                            Payment will be processed after{" "}
                            {formatDate(paymentInfo.cutoffDate.toISOString())}.
                          </>
                        )}
                      </p>
                    ) : !partnership?.partnership.creatorInvoice ? (
                      <div className="space-y-3">
                        <p className="text-sm text-blue-800 dark:text-blue-200">
                          <strong>Invoice Required:</strong>{" "}
                          {creatorId
                            ? "You need to upload your invoice with payment details before payment can be processed. Make sure you have a Stripe Connect account set up in your billing settings so the founder can pay you directly."
                            : "The creator needs to upload their invoice with payment details before you can process payment."}
                        </p>
                        {creatorId &&
                          (partnership?.app?.legalCompanyName ||
                            partnership?.app?.legalCompanyAddress) && (
                            <div className="bg-white/10 p-2 rounded border border-blue-300/20">
                              <p className="text-xs font-medium text-blue-800 dark:text-blue-200 mb-1">
                                Address your invoice to:
                              </p>
                              <div className="space-y-1">
                                {partnership?.app?.legalCompanyName && (
                                  <p className="text-xs text-blue-700 dark:text-blue-300 font-medium">
                                    {partnership.app.legalCompanyName}
                                  </p>
                                )}
                                {partnership?.app?.legalCompanyAddress && (
                                  <p className="text-xs text-blue-700 dark:text-blue-300">
                                    {partnership.app.legalCompanyAddress}
                                  </p>
                                )}
                              </div>
                            </div>
                          )}
                        {creatorId && (
                          <div className="flex items-center gap-2">
                            <Button
                              variant="outline"
                              size="sm"
                              className="text-blue-600 border-blue-200 hover:bg-blue-100 dark:text-blue-400 dark:border-blue-800 dark:hover:bg-blue-900"
                              asChild
                            >
                              <Link
                                to={exampleTemplateImage}
                                target="_blank"
                                rel="noopener noreferrer"
                              >
                                <Eye className="h-4 w-4 mr-2" />
                                View Example Template
                              </Link>
                            </Button>
                          </div>
                        )}
                      </div>
                    ) : creatorId ? (
                      <p className="text-sm text-blue-800 dark:text-blue-200">
                        <strong>Next steps:</strong> The company will review
                        your content performance and process the payment
                        according to the contract terms. You'll receive a
                        notification once payment is sent.
                      </p>
                    ) : (
                      <p className="text-sm text-blue-800 dark:text-blue-200">
                        <strong>Next steps:</strong> Review the creator's
                        performance and process payment through the payouts
                        page. The creator will be notified once payment is
                        completed and can view the receipt here.
                      </p>
                    )}
                  </div>
                </div>
              )}
            </div>
          </Card>
        )}

        {(contractRevisions || []).length > 0 && (
          <Card>
            <div className="p-6">
              <h2 className="text-lg font-medium text-primary">
                Contract Revisions
              </h2>
              <div className="mt-4 space-y-4">
                {(contractRevisions || []).map((revision) => (
                  <div
                    key={revision._id}
                    className="flex flex-col gap-3 rounded-lg bg-secondary p-4"
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm font-medium text-primary">
                          End Date Extension
                        </p>
                        <p className="text-xs text-primary/60">
                          New end date:{" "}
                          {formatRevisionDate(revision.newEndDate)}
                        </p>
                      </div>
                      <div className="text-xs">
                        {revision.status === "pendingCreatorApproval" && (
                          <span className="rounded-full bg-blue-500/10 px-2 py-1 text-blue-500">
                            Pending Creator Approval
                          </span>
                        )}
                        {revision.status === "pendingAppFounderApproval" && (
                          <span className="rounded-full bg-blue-500/10 px-2 py-1 text-blue-500">
                            Pending App Founder Approval
                          </span>
                        )}
                        {revision.status === "approved" && (
                          <span className="rounded-full bg-green-500/10 px-2 py-1 text-green-500">
                            Approved
                          </span>
                        )}
                        {revision.status === "rejected" && (
                          <span className="rounded-full bg-red-500/10 px-2 py-1 text-red-500">
                            Rejected
                          </span>
                        )}
                      </div>
                    </div>

                    {/* Show approve/reject buttons based on user role and revision status */}
                    {revision.status === "pendingCreatorApproval" &&
                      creatorId && (
                        <div className="flex gap-2">
                          <Button
                            variant="outline"
                            className="flex-1"
                            onClick={() =>
                              handleApproveReject(revision._id, "rejected")
                            }
                            disabled={isApprovingRejecting}
                          >
                            {isApprovingRejecting ? (
                              <Loader2 className="h-4 w-4 animate-spin" />
                            ) : (
                              "Reject"
                            )}
                          </Button>
                          <Button
                            className="flex-1"
                            onClick={() =>
                              handleApproveReject(revision._id, "approved")
                            }
                            disabled={isApprovingRejecting}
                          >
                            {isApprovingRejecting ? (
                              <Loader2 className="h-4 w-4 animate-spin" />
                            ) : (
                              "Approve"
                            )}
                          </Button>
                        </div>
                      )}

                    {revision.status === "pendingAppFounderApproval" &&
                      appId && (
                        <div className="flex gap-2">
                          <Button
                            variant="outline"
                            className="flex-1"
                            onClick={() =>
                              handleApproveReject(revision._id, "rejected")
                            }
                            disabled={isApprovingRejecting}
                          >
                            {isApprovingRejecting ? (
                              <Loader2 className="h-4 w-4 animate-spin" />
                            ) : (
                              "Reject"
                            )}
                          </Button>
                          <Button
                            className="flex-1"
                            onClick={() =>
                              handleApproveReject(revision._id, "approved")
                            }
                            disabled={isApprovingRejecting}
                          >
                            {isApprovingRejecting ? (
                              <Loader2 className="h-4 w-4 animate-spin" />
                            ) : (
                              "Approve"
                            )}
                          </Button>
                        </div>
                      )}
                  </div>
                ))}
              </div>
            </div>
          </Card>
        )}

        {creatorId && (
          <Card>
            <div className="p-6">
              <h2 className="text-lg font-medium text-primary">Contact</h2>
              <div className="mt-4 grid grid-cols-1 gap-4">
                <div className="rounded-lg bg-secondary p-4">
                  <p className="text-sm text-primary/60">Name</p>
                  <p className="text-sm font-medium text-primary">
                    {contract.founderContact.name}
                  </p>
                </div>
                <div className="rounded-lg bg-secondary p-4">
                  <p className="text-sm text-primary/60">Email</p>
                  <p className="flex items-center gap-2 text-sm font-medium text-primary">
                    <Mail className="h-4 w-4" />
                    {contract.founderContact.email}
                  </p>
                </div>
                {contract.founderContact.phone && (
                  <div className="rounded-lg bg-secondary p-4">
                    <p className="text-sm text-primary/60">Phone</p>
                    <p className="flex items-center gap-2 text-sm font-medium text-primary">
                      <Phone className="h-4 w-4" />
                      {contract.founderContact.phone}
                    </p>
                  </div>
                )}
              </div>
            </div>
          </Card>
        )}
      </div>
    </div>
  );
}
